/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { ref, set } from 'firebase/database';
import { database } from '../firebase';
import VisionBoard from './VisionBoard';
import ManifestationPage from './ManifestationPage';
import { goalsAPI } from '../firebase';
import '../styles/GoalOnboarding.css';

const GoalOnboarding = ({ user, existingGoal, onComplete, onBack, onSkip, initialStep = 'goal-questions' }) => {
  const [goal, setGoal] = useState('');
  const [description, setDescription] = useState('');
  const [daysUntilNewYear, setDaysUntilNewYear] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(initialStep); // 'goal-questions' | 'vision-upload' | 'manifestation'
  const [goalId, setGoalId] = useState(existingGoal?.id || null);
  const [visionGif, setVisionGif] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Calculate days until January 1st
  useEffect(() => {
    const calculateDaysUntilNewYear = () => {
      const now = new Date();
      const nextYear = now.getFullYear() + 1;
      const newYear = new Date(nextYear, 0, 1); // January 1st of next year
      const timeDiff = newYear - now;
      const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      setDaysUntilNewYear(days);
    };

    calculateDaysUntilNewYear();
    // Update every hour
    const interval = setInterval(calculateDaysUntilNewYear, 3600000);
    return () => clearInterval(interval);
  }, []);

  const handleGoalSubmit = async (e) => {
    e.preventDefault();
    if (!goal.trim() || !description.trim()) return;

    setSaveError(null);
    setIsSubmitting(true);
    try {
      // Use goalsAPI to create goal
      const createResult = await goalsAPI.createGoal(user.uid, {
        goal: goal.trim(),
        description: description.trim(),
        targetDate: new Date(new Date().getFullYear() + 1, 0, 1).toISOString(),
        priority: 'high',
        category: 'professional',
        daysWhenSet: daysUntilNewYear
      });

      if (!createResult.success) {
        throw new Error(createResult.error);
      }

      // Update local state with new goal ID
      setGoalId(createResult.goalId);

      // Move to vision upload
      setCurrentStep('vision-upload');
    } catch (error) {
      setSaveError("We couldn't save your goal — check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVisionUpload = (gifFile) => {
    setVisionGif(gifFile);
  };

  const handleVisionNext = () => {
    setCurrentStep('manifestation');
  };

  const handleCompleteSetup = async (conversationData = null, businessPlanData = null) => {
    try {
      const goalData = {
        id: goalId,
        goal: goal.trim(),
        description: description.trim(),
        createdAt: Date.now(),
        targetDate: new Date(new Date().getFullYear() + 1, 0, 1).getTime(),
        daysWhenSet: daysUntilNewYear
      };

      // Pass goal data, conversation data, and business plan data to parent
      onComplete(goalData, conversationData, businessPlanData);
    } catch (error) {
      console.error('Error completing setup:', error);
    }
  };

  // Initialize with existing goal if provided
  useEffect(() => {
    if (existingGoal) {
      setGoal(existingGoal.title || '');
      setDescription(existingGoal.description || '');
    }
  }, [existingGoal]);

  const userGoalData = {
    goal,
    description,
    id: goalId
  };

  return (
    <div className="goal-onboarding-overlay">
      {currentStep === 'goal-questions' && (
        <div className="create-vision-container">
          <div className="create-vision-header">
            {onBack && (
              <button className="back-to-goals-btn" onClick={onBack}>
                ← Back to Goals
              </button>
            )}
            <h1 className="create-vision-title">Create Vision</h1>
            <div className="countdown-mini">
              <span className="countdown-number-mini">{daysUntilNewYear}</span>
              <span className="countdown-text-mini">days until January 1st</span>
            </div>
          </div>

          <div className="create-vision-content">
            <div className="goal-questions-container">
              <div className="step-header">
                <h2>Define Your Goal</h2>
                <p>Start by clearly defining what you want to achieve and why it matters to you.</p>
              </div>

              <form onSubmit={handleGoalSubmit} className="goal-definition-form">
                <div className="goal-input-group">
                  <label htmlFor="goal" className="goal-label">What is your goal?</label>
                  <input
                    type="text"
                    id="goal"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="e.g., Launch my startup, Write a book, Get promoted..."
                    className="goal-input"
                    maxLength={100}
                    required
                  />
                  <div className="char-counter">{goal.length}/100</div>
                </div>

                <div className="goal-input-group">
                  <label htmlFor="description" className="goal-label">Describe your vision in detail</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What will achieving this goal look like? How will it change your life? Be specific about the outcome you envision..."
                    className="goal-textarea"
                    maxLength={500}
                    required
                  />
                  <div className="char-counter">{description.length}/500</div>
                </div>

                <button
                  type="submit"
                  className="envision-btn"
                  disabled={isSubmitting || !goal.trim() || !description.trim()}
                >
                  {isSubmitting ? 'Creating...' : 'Next: Upload Vision GIF →'}
                </button>

                {saveError && (
                  <div className="goal-save-error" role="alert">
                    {saveError} <button type="submit" className="goal-retry-btn">Try again</button>
                  </div>
                )}
              </form>

              {onSkip && (
                <button
                  type="button"
                  className="onboarding-skip-btn"
                  onClick={async () => {
                    try {
                      await set(ref(database, `users/${user.uid}/journey/onboardingDismissed`), true);
                    } catch (_) {}
                    onSkip();
                  }}
                >
                  I'll explore first
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Single GIF Upload */}
      {currentStep === 'vision-upload' && (
        <div className="create-vision-container">
          <div className="create-vision-header">
            <button className="back-to-goals-btn" onClick={() => setCurrentStep('goal-questions')}>
              ← Back to Questions
            </button>
            <h1 className="create-vision-title">Upload Your Vision</h1>
            <div className="countdown-mini">
              <span className="countdown-number-mini">{daysUntilNewYear}</span>
              <span className="countdown-text-mini">days until January 1st</span>
            </div>
          </div>

          <div className="create-vision-content">
            <div className="single-gif-upload-container">
              <div className="step-header">
                <h2>Visualize Your Success</h2>
                <p>Upload a GIF that represents your goal achieved. This will be your daily inspiration.</p>
              </div>

              <div className="single-gif-upload-area">
                <input
                  type="file"
                  id="vision-gif"
                  accept=".gif,image/gif"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleVisionUpload(file);
                    }
                  }}
                  style={{ display: 'none' }}
                />

                <div
                  className={`gif-drop-zone ${visionGif ? 'has-file' : ''}`}
                  onClick={() => document.getElementById('vision-gif').click()}
                >
                  {visionGif ? (
                    <div className="uploaded-gif-preview">
                      <img
                        src={URL.createObjectURL(visionGif)}
                        alt="Vision GIF"
                        className="vision-gif-display"
                      />
                      <div className="gif-overlay">
                        <p>Click to change GIF</p>
                      </div>
                    </div>
                  ) : (
                    <div className="gif-upload-prompt">
                      <div className="upload-icon">🎬</div>
                      <h3>Upload Your Vision GIF</h3>
                      <p>Drag & drop or click to select a GIF that represents your achieved goal</p>
                      <div className="file-format-hint">Supports .gif files</div>
                    </div>
                  )}
                </div>
              </div>

              <button
                className="envision-btn"
                onClick={handleVisionNext}
              >
                {visionGif ? 'Continue' : 'Skip for now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: AI Dev Mentor / Business Planning */}
      {currentStep === 'manifestation' && goalId && (
        <ManifestationPage
          user={user}
          userGoal={userGoalData}
          onComplete={handleCompleteSetup}
          onBack={() => setCurrentStep('vision-upload')}
        />
      )}
    </div>
  );
};

export default GoalOnboarding;
